<template>
  <div class="date-range-wrapper">
    <VueDatePicker
      v-model="startPickerRange"
      range
      :enable-time-picker="false"
      auto-apply
      :teleport="true"
      :min-date="minDate"
      :max-date="maxDate"
      :text-input="true"
      :text-input-options="textInputOptions"
      :clearable="false"
      @update:model-value="onStartPickerChange"
    >
      <template #dp-input="{ onEnter, onTab, onBlur, onKeypress, onPaste }">
        <input
          class="date-field"
          :value="startDate"
          :placeholder="startPlaceholder"
          @change="onStartManualInput"
          @keydown.enter="onEnter"
          @keydown.tab="onTab"
          @blur="onBlur"
          @keypress="onKeypress"
          @paste="onPaste"
        />
      </template>
    </VueDatePicker>

    <span class="date-separator">-</span>

    <VueDatePicker
      v-model="endPickerRange"
      range
      :enable-time-picker="false"
      auto-apply
      :teleport="true"
      :min-date="minDate"
      :max-date="maxDate"
      :text-input="true"
      :text-input-options="textInputOptions"
      :clearable="false"
      @update:model-value="onEndPickerChange"
    >
      <template #dp-input="{ onEnter, onTab, onBlur, onKeypress, onPaste }">
        <input
          class="date-field"
          :value="endDate"
          :placeholder="endPlaceholder"
          @change="onEndManualInput"
          @keydown.enter="onEnter"
          @keydown.tab="onTab"
          @blur="onBlur"
          @keypress="onKeypress"
          @paste="onPaste"
        />
      </template>
    </VueDatePicker>
  </div>
</template>

<script>
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export default {
  name: "DateRangePicker",

  props: {
    modelValue: {
      type: Array,
      default: () => [new Date(), new Date()],
    },
    startPlaceholder: {
      type: String,
      default: "Start Date",
    },
    endPlaceholder: {
      type: String,
      default: "End Date",
    },
    displayFormat: {
      type: String,
      default: "YYYY-MM-DD",
    },
    minDate: {
      type: String,
      default: null,
    },
    maxDate: {
      type: String,
      default: null,
    },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      range: [null, null],
    };
  },
  computed: {
    startPickerRange: {
      get() {
        return this.range;
      },
      set(val) {
        this.range = val ?? [null, null];
      },
    },
    endPickerRange: {
      get() {
        return this.range;
      },
      set(val) {
        this.range = val ?? [null, null];
      },
    },
    startDate() {
      return this.range?.[0]
        ? dayjs(this.range[0]).format(this.displayFormat)
        : "";
    },
    endDate() {
      return this.range?.[1]
        ? dayjs(this.range[1]).format(this.displayFormat)
        : "";
    },
    textInputOptions() {
      return { format: this.displayFormat };
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        this.range = val?.length === 2 ? [...val] : [null, null];
      },
    },
  },
  methods: {
    onStartPickerChange(val) {
      if (!val) return;
      const newStart = val[0] ?? this.range[0];
      const newEnd = val[1] ?? this.range[1];
      this.updateRange([newStart, newEnd]);
    },
    onEndPickerChange(val) {
      if (!val) return;
      const newStart = val[0] ?? this.range[0];
      const newEnd = val[1] ?? this.range[1];
      this.updateRange([newStart, newEnd]);
    },
    onStartManualInput(e) {
      const parsed = dayjs(e.target.value, this.displayFormat, true);
      if (!parsed.isValid()) return;
      this.updateRange([parsed.toDate(), this.range[1]]);
    },
    onEndManualInput(e) {
      const parsed = dayjs(e.target.value, this.displayFormat, true);
      if (!parsed.isValid()) return;
      if (this.range[0] && parsed.isBefore(dayjs(this.range[0]))) return;
      this.updateRange([this.range[0], parsed.toDate()]);
    },
    updateRange(val) {
      this.range = val;
      if (val[0] && val[1]) {
        this.$emit("update:modelValue", val);
        this.$emit("change", val);
      }
    },
  },
};
</script>

<style scoped>
.date-range-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.date-field {
  width: 100%;
  cursor: pointer;
  border: 1.5px solid #d1d5db;
  border-radius: 20px;
  padding: 8px 16px 8px 35px;
  font-size: 14px;
  font-weight: 500;
  color: #222;
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease;
  white-space: nowrap;
}

.date-field::placeholder {
  color: #aaa;
  font-weight: 400;
}

.date-field:hover {
  border-color: #999;
}

.date-separator {
  color: #555;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}
</style>
